import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Button } from './Button';
import { api } from '../../services/api';
import { trackEvent } from '../../utils/analytics';

export interface FeedbackRatingOption {
  value: string;
  emoji?: string;
  label: string;
}

interface FeedbackSurveyModalProps {
  /** Survey key (must be allowlisted on the backend), e.g. 'ranking_stats_panel'. */
  feature: string;
  title: string;
  subtitle?: string;
  /** Omit to make the survey comment-only. */
  ratingOptions?: FeedbackRatingOption[];
  commentLabel: string;
  commentPlaceholder?: string;
  /** Called after the answer is successfully saved. */
  onSubmitted: () => void;
}

/**
 * Generic, blocking, one-time feedback survey. There is no close/skip — the
 * user must answer to dismiss it. Reusable across features via the `feature`
 * key + copy props.
 */
export function FeedbackSurveyModal({
  feature,
  title,
  subtitle,
  ratingOptions,
  commentLabel,
  commentPlaceholder = 'Escreva sua resposta...',
  onSubmitted,
}: FeedbackSurveyModalProps) {
  const [rating, setRating] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lock body scroll while open.
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const ratingOk = !ratingOptions || rating !== null;
  const commentOk = comment.trim().length >= 2;
  const canSubmit = ratingOk && commentOk && !isSubmitting;

  const handleSubmit = async () => {
    if (!ratingOk || !commentOk) {
      setError(
        ratingOptions
          ? 'Escolha uma opção e escreva um comentário.'
          : 'Escreva um comentário.'
      );
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await api.vision.submitFeedback(feature, rating, comment.trim());
      if (res.success) {
        trackEvent('btv_feedback_submit', { feature, rating });
        onSubmitted();
      } else {
        setError('Não foi possível enviar. Tente novamente.');
        setIsSubmitting(false);
      }
    } catch {
      setError('Não foi possível enviar. Tente novamente.');
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/80 backdrop-blur-sm"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-full max-w-md bg-gradient-to-br from-turquoise/10 to-deep-blue border border-white/10 rounded-3xl p-6 shadow-2xl"
      >
        <h2 className="text-xl font-bold text-white text-center mb-1">{title}</h2>
        {subtitle && (
          <p className="text-white/50 text-center text-sm mb-5">{subtitle}</p>
        )}

        {ratingOptions && (
          <div
            className="grid gap-2 mb-5"
            style={{ gridTemplateColumns: `repeat(${ratingOptions.length}, minmax(0, 1fr))` }}
          >
            {ratingOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setRating(opt.value)}
                className={`flex flex-col items-center gap-1.5 px-2 py-3 rounded-2xl border transition-colors ${
                  rating === opt.value
                    ? 'border-turquoise bg-turquoise/15 text-white'
                    : 'border-white/10 bg-white/5 text-white/60 hover:text-white hover:border-white/20'
                }`}
              >
                {opt.emoji && <span className="text-2xl">{opt.emoji}</span>}
                <span className="text-xs font-medium text-center leading-tight">{opt.label}</span>
              </button>
            ))}
          </div>
        )}

        <label className="block text-sm text-white/70 mb-2">{commentLabel}</label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={2000}
          rows={3}
          placeholder={commentPlaceholder}
          className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise resize-none mb-2"
        />

        {error && <p className="text-sunny text-sm mb-3">{error}</p>}

        <Button onClick={handleSubmit} disabled={!canSubmit} isLoading={isSubmitting} fullWidth>
          Enviar
        </Button>
      </motion.div>
    </motion.div>
  );
}
